import { useCallback } from 'react';
import type { ScopeLevel } from '@/lib/shared-types';
import { useAuth } from '@/Hooks/useAuth';

/**
 * Broad-to-narrow, exactly as `common/guards/scope.guard.ts` orders it.
 */
const SCOPE_HIERARCHY: ScopeLevel[] = [
  'PLATFORM',
  'TENANT',
  // ADR-0023 §3.1 — a brand never leaves its tenant, so this is one more rung
  // between TENANT and BRANCH, not a second hierarchy. Omitting it here would
  // have made `indexOf('BRAND')` return -1 for both a BRAND grant's own depth
  // and a route's BRAND requirement, so the `depth > required` / `depth <
  // required` checks below would have silently skipped straight to the
  // narrowest branch — coincidentally correct for a BRAND-scoped user judging
  // their own brand, but WRONG for a broader TENANT grant, which must cover a
  // BRAND target and would instead have been refused.
  'BRAND',
  'BRANCH',
  'FLOOR',
  'AREA',
  'TABLE',
  'RESOURCE',
];

/**
 * Client mirror of `ScopeGuard`, for the 37 routes that carry `@RequireScope(...)`
 * — branch, floor, area and table writes, the floor-map save and publish, table
 * state, session open, device pairing and every Wi-Fi write.
 *
 * A permission key alone does not decide those: `settings.update` held only at
 * Branch-1 is refused for Branch-2, so a control aimed there is a guaranteed 403
 * rather than a missing permission. The rule is the guard's: a grant at a
 * broader level covers everything beneath it, an equal-level grant covers only
 * its own id, and a narrower grant covers nothing above it. Platform admins are
 * exempt, as they are server-side.
 *
 * It lives beside `usePermission()` because it answers the other half of the
 * same question — permission is *what*, scope is *where* — and both Settings and
 * Spaces need it. Like `usePermission()` it is presentation only: the server
 * stays authoritative and this merely stops the client offering an action that
 * is certain to be refused.
 */
export function useRequireScope() {
  const { user } = useAuth();

  return useCallback(
    (level: ScopeLevel, targetId?: string | null): boolean => {
      if (!user) return false;
      if (user.isPlatformAdmin) return true;

      const required = SCOPE_HIERARCHY.indexOf(level);
      return (user.scopes ?? []).some((scope) => {
        const depth = SCOPE_HIERARCHY.indexOf(scope.level);
        if (depth > required) return false;
        if (depth < required) return true;
        return !targetId || scope.id === null || scope.id === targetId;
      });
    },
    [user],
  );
}

export default useRequireScope;
